import { useEffect, useMemo, useState, useCallback } from 'react';
import {
    CalendarCheck,
    Search,
    Loader2,
    Mail,
    AlertTriangle,
    CheckCircle2,
    Clock,
    XCircle,
    ChevronRight,
    User,
    Building2,
    DollarSign,
    Inbox,
    RefreshCw,
} from 'lucide-react';
import {
    demoRequestsApi,
    type DemoRequestSummary,
    type DemoRequestDetail,
    type DemoRequestStatus,
} from '../lib/api/demoRequests';

const STATUS_OPTIONS: { value: DemoRequestStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'booked', label: 'Booked' },
    { value: 'completed', label: 'Completed' },
    { value: 'no_show', label: 'No-show' },
    { value: 'rejected', label: 'Rejected' },
];

const STATUS_STYLE: Record<DemoRequestStatus, { bg: string; text: string; label: string }> = {
    new: { bg: 'bg-amber-50 border-amber-100', text: 'text-amber-600', label: 'New' },
    contacted: { bg: 'bg-blue-50 border-blue-100', text: 'text-blue-600', label: 'Contacted' },
    booked: { bg: 'bg-emerald-50 border-emerald-100', text: 'text-emerald-600', label: 'Booked' },
    completed: { bg: 'bg-neutral-100 border-neutral-200', text: 'text-neutral-600', label: 'Completed' },
    no_show: { bg: 'bg-rose-50 border-rose-100', text: 'text-rose-600', label: 'No-show' },
    rejected: { bg: 'bg-red-50 border-red-100', text: 'text-red-600', label: 'Rejected' },
};

const REVENUE_LABELS: Record<string, string> = {
    under_10k: 'Under $10k',
    '10k_50k': '$10k – $50k',
    '50k_250k': '$50k – $250k',
    '250k_1m': '$250k – $1M',
    over_1m: 'Over $1M',
};

const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const StatusBadge: React.FC<{ status: DemoRequestStatus }> = ({ status }) => {
    const style = STATUS_STYLE[status];
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${style.bg} ${style.text}`}
        >
            {style.label}
        </span>
    );
};

const EmptyState: React.FC<{ filtered: boolean }> = ({ filtered }) => (
    <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-4 text-neutral-400">
            <Inbox size={26} />
        </div>
        <h3 className="text-sm font-semibold text-neutral-700 mb-1">
            {filtered ? 'No demo requests match these filters' : 'No demo requests yet'}
        </h3>
        <p className="text-xs text-neutral-500 max-w-xs">
            {filtered
                ? 'Try clearing the search or status filter to see other requests.'
                : 'When someone fills the /demo form on the marketing site, it will appear here.'}
        </p>
    </div>
);

const DetailDrawer: React.FC<{
    detail: DemoRequestDetail;
    onClose: () => void;
    onStatusChange: (status: DemoRequestStatus) => Promise<void>;
    isUpdating: boolean;
}> = ({ detail, onClose, onStatusChange, isUpdating }) => {
    const channels = detail.channels.length ? detail.channels.join(', ') : '—';
    const revenue = detail.monthlyRevenue
        ? REVENUE_LABELS[detail.monthlyRevenue] ?? detail.monthlyRevenue
        : '—';

    return (
        <div className="fixed inset-0 z-40 flex">
            <div
                className="absolute inset-0 bg-neutral-900/40 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden
            />
            <aside className="relative ml-auto h-full w-full max-w-2xl bg-white shadow-2xl border-l border-neutral-200 flex flex-col">
                <header className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-neutral-200">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <StatusBadge status={detail.status} />
                                <span className="text-[11px] text-neutral-400 font-mono">
                                    {detail.id}
                                </span>
                            </div>
                            <h2 className="text-xl font-bold text-neutral-900 truncate">
                                {detail.name}
                            </h2>
                            <p className="text-sm text-neutral-500 truncate">
                                {detail.company} · {detail.email}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg"
                            aria-label="Close"
                        >
                            <XCircle size={18} />
                        </button>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <label className="text-[11px] uppercase tracking-wider text-neutral-400 font-bold">
                            Status:
                        </label>
                        {(['new', 'contacted', 'booked', 'completed', 'no_show', 'rejected'] as DemoRequestStatus[]).map((s) => {
                            const isActive = detail.status === s;
                            return (
                                <button
                                    key={s}
                                    disabled={isActive || isUpdating}
                                    onClick={() => onStatusChange(s)}
                                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${isActive
                                        ? 'bg-neutral-900 text-white border-neutral-900 cursor-default'
                                        : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400 disabled:opacity-50'
                                        }`}
                                >
                                    {STATUS_STYLE[s].label}
                                </button>
                            );
                        })}
                        {isUpdating && <Loader2 size={14} className="animate-spin text-neutral-400" />}
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                    {/* Submission detail */}
                    <section>
                        <h3 className="text-[10px] uppercase tracking-widest font-bold text-neutral-400 mb-3">
                            Submission
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <Field icon={<User size={14} />} label="Role" value={detail.role || '—'} />
                            <Field icon={<Building2 size={14} />} label="Team size" value={detail.teamSize || '—'} />
                            <Field icon={<DollarSign size={14} />} label="Monthly revenue" value={revenue} />
                            <Field icon={<CalendarCheck size={14} />} label="Preferred slot" value={detail.preferredSlot || '—'} />
                            <Field icon={<Mail size={14} />} label="Channels" value={channels} colSpan={2} />
                            {detail.message && (
                                <Field icon={<Inbox size={14} />} label="Message" value={detail.message} colSpan={2} />
                            )}
                        </div>
                    </section>

                    <section>
                        <h3 className="text-[10px] uppercase tracking-widest font-bold text-neutral-400 mb-3">
                            Timing
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Submitted" value={formatDateTime(detail.createdAt)} />
                            <Field label="Updated" value={formatDateTime(detail.updatedAt)} />
                        </div>
                    </section>

                    {/* Email log */}
                    <section>
                        <h3 className="text-[10px] uppercase tracking-widest font-bold text-neutral-400 mb-3">
                            Emails ({detail.emails.length})
                        </h3>
                        {detail.emails.length === 0 ? (
                            <div className="text-xs text-neutral-500 px-3 py-4 border border-dashed border-neutral-200 rounded-lg text-center">
                                No emails recorded for this request yet.
                            </div>
                        ) : (
                            <ol className="space-y-3">
                                {detail.emails.map((e) => {
                                    const ok = e.status === 'sent';
                                    return (
                                        <li
                                            key={e.id}
                                            className="border border-neutral-200 rounded-xl p-4 bg-neutral-50/40"
                                        >
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <div className="flex items-center gap-2">
                                                    {ok ? (
                                                        <CheckCircle2 size={14} className="text-emerald-500" />
                                                    ) : (
                                                        <AlertTriangle size={14} className="text-red-500" />
                                                    )}
                                                    <span className="text-xs font-semibold text-neutral-700">
                                                        {e.subject}
                                                    </span>
                                                </div>
                                                <span className="text-[10px] text-neutral-400 flex items-center gap-1">
                                                    <Clock size={11} />
                                                    {formatDateTime(e.sentAt)}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-neutral-500 mb-2">
                                                <span className="font-medium text-neutral-600">To:</span> {e.toAddress}
                                                {e.fromAddress && (
                                                    <>
                                                        {' '}· <span className="font-medium text-neutral-600">From:</span> {e.fromAddress}
                                                    </>
                                                )}
                                                {' '}· <span className={`font-bold ${ok ? 'text-emerald-600' : 'text-red-600'}`}>{e.status}</span>
                                            </div>
                                            {e.errorMessage && (
                                                <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-md px-2 py-1.5 mb-2">
                                                    {e.errorMessage}
                                                </div>
                                            )}
                                            <details className="text-[11px] text-neutral-600 group">
                                                <summary className="cursor-pointer text-neutral-500 hover:text-neutral-700 select-none">
                                                    Show body preview
                                                </summary>
                                                <iframe
                                                    title={`email-${e.id}`}
                                                    sandbox=""
                                                    srcDoc={e.htmlPreview}
                                                    className="mt-2 w-full h-72 rounded-md border border-neutral-200 bg-white"
                                                />
                                            </details>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </section>
                </div>
            </aside>
        </div>
    );
};

const Field: React.FC<{ label: string; value: string; icon?: React.ReactNode; colSpan?: number }> = ({
    label,
    value,
    icon,
    colSpan = 1,
}) => (
    <div className={colSpan === 2 ? 'col-span-2' : ''}>
        <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1 flex items-center gap-1">
            {icon}
            {label}
        </div>
        <div className="text-sm text-neutral-800 break-words whitespace-pre-wrap">{value}</div>
    </div>
);

export const DemoRequestsPage: React.FC = () => {
    const [items, setItems] = useState<DemoRequestSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState<DemoRequestStatus | 'all'>('all');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [detail, setDetail] = useState<DemoRequestDetail | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // Debounce search input by 300ms
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const refreshList = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await demoRequestsApi.list({
                status: statusFilter,
                search: debouncedSearch || undefined,
                limit: 100,
            });
            setItems(res.items);
            setTotal(res.total);
        } catch (err) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Failed to load demo requests';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    }, [statusFilter, debouncedSearch]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    const openDetail = useCallback(async (id: string) => {
        setSelectedId(id);
        setIsLoadingDetail(true);
        try {
            const data = await demoRequestsApi.get(id);
            setDetail(data);
        } catch (err) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Failed to load request details';
            setError(message);
            setSelectedId(null);
        } finally {
            setIsLoadingDetail(false);
        }
    }, []);

    const closeDetail = useCallback(() => {
        setSelectedId(null);
        setDetail(null);
    }, []);

    const handleStatusChange = useCallback(
        async (status: DemoRequestStatus) => {
            if (!detail) return;
            setIsUpdatingStatus(true);
            try {
                await demoRequestsApi.updateStatus(detail.id, status);
                // Refresh both detail and list
                const fresh = await demoRequestsApi.get(detail.id);
                setDetail(fresh);
                await refreshList();
            } catch (err) {
                const message =
                    (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                    'Failed to update status';
                setError(message);
            } finally {
                setIsUpdatingStatus(false);
            }
        },
        [detail, refreshList],
    );

    const filtered = statusFilter !== 'all' || debouncedSearch.length > 0;

    const counts = useMemo(() => {
        const acc: Partial<Record<DemoRequestStatus | 'all', number>> = { all: total };
        items.forEach((i) => {
            acc[i.status] = (acc[i.status] || 0) + 1;
        });
        return acc;
    }, [items, total]);

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Page header */}
            <div className="flex-shrink-0 px-6 py-5 border-b border-neutral-200">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="w-9 h-9 rounded-xl bg-accent-primary/10 text-accent-primary flex items-center justify-center">
                                <CalendarCheck size={18} />
                            </div>
                            <h1 className="text-xl font-bold text-neutral-900">Demo Requests</h1>
                        </div>
                        <p className="text-sm text-neutral-500 ml-12">
                            Bookings submitted from the public /demo page, with the email log for each one.
                        </p>
                    </div>
                    <button
                        onClick={refreshList}
                        disabled={isLoading}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border border-neutral-200 rounded-lg text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
                    >
                        {isLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Refresh
                    </button>
                </div>

                {/* Filters */}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1 border border-neutral-200">
                        {STATUS_OPTIONS.map((opt) => {
                            const isActive = statusFilter === opt.value;
                            const count = counts[opt.value];
                            return (
                                <button
                                    key={opt.value}
                                    onClick={() => setStatusFilter(opt.value)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5 ${isActive
                                        ? 'bg-white shadow-sm text-neutral-900'
                                        : 'text-neutral-500 hover:text-neutral-700'
                                        }`}
                                >
                                    {opt.label}
                                    {typeof count === 'number' && (
                                        <span className="text-[10px] text-neutral-400 font-mono">
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex-1 min-w-[200px] relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, email or company"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                        />
                    </div>
                </div>

                {error && (
                    <div className="mt-3 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs">
                        {error}
                    </div>
                )}
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
                {isLoading && items.length === 0 ? (
                    <div className="flex items-center justify-center py-20 text-neutral-400">
                        <Loader2 size={20} className="animate-spin" />
                    </div>
                ) : items.length === 0 ? (
                    <EmptyState filtered={filtered} />
                ) : (
                    <ul className="divide-y divide-neutral-100">
                        {items.map((req) => (
                            <li
                                key={req.id}
                                onClick={() => openDetail(req.id)}
                                className="px-6 py-4 hover:bg-neutral-50 cursor-pointer flex items-center gap-4 group"
                            >
                                <div className="w-10 h-10 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center font-bold text-sm shrink-0">
                                    {(req.name?.[0] || '?').toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className="font-semibold text-neutral-900 text-sm truncate">
                                            {req.name}
                                        </span>
                                        <StatusBadge status={req.status} />
                                    </div>
                                    <div className="text-xs text-neutral-500 truncate">
                                        {req.company} · {req.email}
                                        {req.role && <> · {req.role}</>}
                                    </div>
                                </div>
                                <div className="hidden md:flex flex-col items-end shrink-0 text-right">
                                    <span className="text-[11px] text-neutral-400 flex items-center gap-1">
                                        <Clock size={11} />
                                        {formatDateTime(req.createdAt)}
                                    </span>
                                    {typeof req.emailCount === 'number' && req.emailCount > 0 && (
                                        <span className="text-[10px] text-neutral-400 mt-0.5 flex items-center gap-1">
                                            <Mail size={10} />
                                            {req.emailCount} email{req.emailCount === 1 ? '' : 's'}
                                        </span>
                                    )}
                                </div>
                                <ChevronRight size={16} className="text-neutral-300 group-hover:text-neutral-500 shrink-0" />
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {selectedId && (
                isLoadingDetail || !detail ? (
                    <div className="fixed inset-0 z-40 flex items-center justify-center bg-neutral-900/30">
                        <div className="bg-white rounded-xl px-5 py-4 flex items-center gap-2 text-sm text-neutral-600 shadow-xl">
                            <Loader2 size={16} className="animate-spin" />
                            Loading…
                        </div>
                    </div>
                ) : (
                    <DetailDrawer
                        detail={detail}
                        onClose={closeDetail}
                        onStatusChange={handleStatusChange}
                        isUpdating={isUpdatingStatus}
                    />
                )
            )}
        </div>
    );
};
